import { useEffect, useState } from 'react';
import { Database, HardDrive, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../../ui/button';
import { Progress } from '../../ui/progress';
import { RUNTIME_BACKEND_URL } from '../../../lib/backendUrl';

export const formatBytes = (b) => {
  if (!b) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(b) / Math.log(1024)), u.length - 1);
  return `${(b / 1024 ** i).toLocaleString('tr-TR', { maximumFractionDigits: i ? 2 : 0 })} ${u[i]}`;
};

const HESAP_COLUMNS = [
  { key: 'db_bytes', label: 'Veritabanı', sub: r => `${(r.records || 0).toLocaleString('tr-TR')} kayıt` },
  { key: 'cloud_bytes', label: 'Bulut', sub: r => `${r.cloud_files || 0} dosya` },
  { key: 'evrak_bytes', label: 'Apartman Evrak' },
  { key: 'image_bytes', label: 'Servis Resimleri' },
];

const Summary = ({ data }) => {
  const diskPct = data.disk?.total ? Math.round((data.disk.used / data.disk.total) * 100) : 0;
  const db = data.database || {};
  return (
    <div className="grid sm:grid-cols-3 gap-3">
      <div className="rounded-xl border p-4" data-testid="storage-total-users">
        <p className="text-xs text-slate-500">Kullanıcıların toplam kullanımı</p>
        <p className="text-2xl font-bold mt-1">{formatBytes(data.total_bytes)}</p>
      </div>
      <div className="rounded-xl border p-4" data-testid="storage-db-size">
        <p className="text-xs text-slate-500 flex items-center gap-1"><Database className="h-3.5 w-3.5" /> Veritabanı</p>
        <p className="text-2xl font-bold mt-1">{formatBytes(db.data_size)}</p>
        <p className="text-[10px] text-slate-500 mt-1">{(db.objects || 0).toLocaleString('tr-TR')} kayıt · {db.collections || 0} tablo</p>
      </div>
      <div className="rounded-xl border p-4" data-testid="storage-disk">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span className="flex items-center gap-1"><HardDrive className="h-3.5 w-3.5" /> Sunucu diski</span>
          <span>%{diskPct}</span>
        </div>
        <Progress value={diskPct} className="mt-3 h-2" />
        <p className="text-[10px] text-slate-500 mt-2">{formatBytes(data.disk?.used)} / {formatBytes(data.disk?.total)} · Boş: <b>{formatBytes(data.disk?.free)}</b></p>
      </div>
    </div>
  );
};

export const StorageTab = ({ token, endpoint = '/api/hesap/admin/storage-usage', columns = HESAP_COLUMNS }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${RUNTIME_BACKEND_URL}${endpoint}`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setData(await r.json());
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  if (!data) return <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <Summary data={data} />
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="storage-refresh-btn">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Yenile
        </Button>
      </div>
      <div className="overflow-x-auto border rounded-xl">
        <table className="w-full text-sm" data-testid="storage-table">
          <thead className="bg-slate-50 text-xs text-slate-500">
            <tr>
              <th className="text-left p-3">Kullanıcı</th>
              {columns.map(c => <th key={c.key} className="text-right p-3 whitespace-nowrap">{c.label}</th>)}
              <th className="text-right p-3">Toplam</th>
            </tr>
          </thead>
          <tbody>
            {data.users.map(u => (
              <tr key={u.user_id} className="border-t" data-testid={`storage-row-${u.user_id}`}>
                <td className="p-3">
                  <p className="font-medium">{u.full_name || '-'}</p>
                  <p className="text-xs text-slate-500">{u.email}</p>
                </td>
                {columns.map(c => (
                  <td key={c.key} className="p-3 text-right whitespace-nowrap">
                    {formatBytes(u[c.key])}
                    {c.sub && <span className="block text-[10px] text-slate-400">{c.sub(u)}</span>}
                  </td>
                ))}
                <td className="p-3 text-right whitespace-nowrap font-semibold">{formatBytes(u.total_bytes)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
